import React, { useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { ChevronRight } from 'lucide-react';
import api from '../../utils/api';
import { useTenant } from '../../context/TenantContext';

const CATEGORIAS = [
  { value: 'suministros', label: 'Suministros (luz, agua, gas, etc.)' },
  { value: 'alquiler', label: 'Alquiler y locales' },
  { value: 'marketing', label: 'Marketing y publicidad' },
  { value: 'servicios', label: 'Servicios profesionales' },
  { value: 'material', label: 'Material de oficina' },
  { value: 'viajes', label: 'Viajes y dietas' },
  { value: 'seguros', label: 'Seguros' },
  { value: 'otros', label: 'Otros' },
];

const TIPOS_IVA = [
  { value: '21', label: '21%' },
  { value: '10', label: '10%' },
  { value: '4', label: '4%' },
  { value: '0', label: '0%' },
  { value: 'no_sujeto', label: 'No sujeto' },
];

const METODOS_PAGO = [
  { value: 'transferencia', label: 'Transferencia bancaria' },
  { value: 'tarjeta', label: 'Tarjeta' },
  { value: 'efectivo', label: 'Efectivo' },
  { value: 'domiciliacion', label: 'Domiciliación' },
];

export default function RegistrarGastoForm({ initialData, onSubmit }) {
  const { activeTenant } = useTenant();
  const [catalogVendors, setCatalogVendors] = useState([]);
  const [selectedVendorId, setSelectedVendorId] = useState('');
  const [formData, setFormData] = useState({
    proveedor_nombre: initialData?.proveedor_nombre || '',
    proveedor_nif: initialData?.proveedor_nif || '',
    proveedor_nuevo: initialData?.proveedor_nuevo || false,
    numero_factura: initialData?.numero_factura || '',
    fecha_factura: initialData?.fecha_factura || new Date().toISOString().split('T')[0],
    fecha_vencimiento: initialData?.fecha_vencimiento || '',
    concepto: initialData?.concepto || '',
    categoria_gasto: initialData?.categoria_gasto || 'servicios',
    base_imponible: initialData?.base_imponible || 0,
    tipo_iva: initialData?.tipo_iva || '21',
    importe_iva: initialData?.importe_iva || 0,
    tiene_retencion: initialData?.tiene_retencion || false,
    importe_retencion: initialData?.importe_retencion || 0,
    total_factura: initialData?.total_factura || 0,
    metodo_pago: initialData?.metodo_pago || 'transferencia',
    pagado: initialData?.pagado || false,
    fecha_pago: initialData?.fecha_pago || '',
    observaciones: initialData?.observaciones || '',
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!activeTenant) return;
    api.get(`/catalog/vendors?tenant_id=${activeTenant}`)
      .then((res) => setCatalogVendors(res.data || []))
      .catch(() => setCatalogVendors([]));
  }, [activeTenant]);

  const handleChange = (field, value) => {
    const newData = { ...formData, [field]: value };
    
    // Auto-calculate IVA and total
    if (field === 'base_imponible' || field === 'tipo_iva') {
      const base = field === 'base_imponible' ? value : newData.base_imponible;
      const tipoIva = field === 'tipo_iva' ? value : newData.tipo_iva;
      const ivaRate = tipoIva === 'no_sujeto' ? 0 : parseFloat(tipoIva) / 100;
      newData.importe_iva = parseFloat((base * ivaRate).toFixed(2));
      newData.total_factura = parseFloat((base + newData.importe_iva - (newData.tiene_retencion ? newData.importe_retencion : 0)).toFixed(2));
    }
    
    if (field === 'tiene_retencion' || field === 'importe_retencion') {
      const retencion = newData.tiene_retencion ? (field === 'importe_retencion' ? value : newData.importe_retencion) : 0;
      newData.total_factura = parseFloat((newData.base_imponible + newData.importe_iva - retencion).toFixed(2));
    }
    
    setFormData(newData);
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: null }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.proveedor_nombre) newErrors.proveedor_nombre = 'Requerido';
    if (!formData.proveedor_nif) newErrors.proveedor_nif = 'Requerido';
    if (!formData.numero_factura) newErrors.numero_factura = 'Requerido';
    if (!formData.concepto) newErrors.concepto = 'Requerido';
    if (formData.base_imponible <= 0) newErrors.base_imponible = 'Debe ser mayor a 0';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) onSubmit(formData);
  };

  const handleSelectVendor = (vendorId) => {
    setSelectedVendorId(vendorId);
    const vendor = catalogVendors.find((item) => item.id === vendorId);
    if (!vendor) return;
    handleChange('proveedor_nombre', vendor.name || '');
    handleChange('proveedor_nif', vendor.nif || '');
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-4">
        <h3 className="font-semibold text-slate-900 border-b pb-2">Datos del Proveedor</h3>
        {catalogVendors.length > 0 && (
          <div>
            <Label>Proveedor registrado</Label>
            <Select value={selectedVendorId} onValueChange={handleSelectVendor}>
              <SelectTrigger data-testid="select-proveedor-catalogo">
                <SelectValue placeholder="Selecciona un proveedor" />
              </SelectTrigger>
              <SelectContent>
                {catalogVendors.map((vendor) => (
                  <SelectItem key={vendor.id} value={vendor.id}>
                    {vendor.name} {vendor.nif ? `(${vendor.nif})` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Nombre del Proveedor *</Label>
            <Input
              value={formData.proveedor_nombre}
              onChange={(e) => handleChange('proveedor_nombre', e.target.value)}
              placeholder="Razón social del proveedor"
              className={errors.proveedor_nombre ? 'border-red-500' : ''}
              data-testid="input-proveedor-nombre"
            />
          </div>
          <div>
            <Label>NIF/CIF *</Label>
            <Input
              value={formData.proveedor_nif}
              onChange={(e) => handleChange('proveedor_nif', e.target.value.toUpperCase())}
              className={errors.proveedor_nif ? 'border-red-500' : ''}
              data-testid="input-proveedor-nif"
            />
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <Checkbox
            id="proveedor_nuevo"
            checked={formData.proveedor_nuevo}
            onCheckedChange={(checked) => handleChange('proveedor_nuevo', checked)}
          />
          <Label htmlFor="proveedor_nuevo" className="cursor-pointer">Es un proveedor nuevo (dar de alta)</Label>
        </div>
      </div>

      <div className="space-y-4">
        <h3 className="font-semibold text-slate-900 border-b pb-2">Datos de la Factura</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label>Nº Factura *</Label>
            <Input
              value={formData.numero_factura}
              onChange={(e) => handleChange('numero_factura', e.target.value)}
              placeholder="F-2025-001"
              className={errors.numero_factura ? 'border-red-500' : ''}
              data-testid="input-numero-factura"
            />
          </div>
          <div>
            <Label>Fecha Factura *</Label>
            <Input
              type="date"
              value={formData.fecha_factura}
              onChange={(e) => handleChange('fecha_factura', e.target.value)}
            />
          </div>
          <div>
            <Label>Fecha Vencimiento</Label>
            <Input
              type="date"
              value={formData.fecha_vencimiento}
              onChange={(e) => handleChange('fecha_vencimiento', e.target.value)}
            />
          </div>
        </div>

        <div>
          <Label>Concepto *</Label>
          <Input
            value={formData.concepto}
            onChange={(e) => handleChange('concepto', e.target.value)}
            placeholder="Descripción del gasto"
            className={errors.concepto ? 'border-red-500' : ''}
            data-testid="input-concepto"
          />
        </div>

        <div>
          <Label>Categoría *</Label>
          <Select value={formData.categoria_gasto} onValueChange={(v) => handleChange('categoria_gasto', v)}>
            <SelectTrigger data-testid="select-categoria">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CATEGORIAS.map(c => (
                <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-4">
        <h3 className="font-semibold text-slate-900 border-b pb-2">Importes</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label>Base Imponible (€) *</Label>
            <Input
              type="number"
              step="0.01"
              min="0"
              value={formData.base_imponible}
              onChange={(e) => handleChange('base_imponible', parseFloat(e.target.value) || 0)}
              className={errors.base_imponible ? 'border-red-500' : ''}
              data-testid="input-base-imponible"
            />
          </div>
          <div>
            <Label>Tipo IVA *</Label>
            <Select value={formData.tipo_iva} onValueChange={(v) => handleChange('tipo_iva', v)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TIPOS_IVA.map(t => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Importe IVA (€)</Label>
            <Input
              type="number"
              value={formData.importe_iva}
              readOnly
              className="bg-slate-50"
            />
          </div>
        </div>

        <div className="flex items-center space-x-2">
          <Checkbox
            id="tiene_retencion"
            checked={formData.tiene_retencion}
            onCheckedChange={(checked) => handleChange('tiene_retencion', checked)}
          />
          <Label htmlFor="tiene_retencion" className="cursor-pointer">La factura tiene retención IRPF</Label>
          {formData.tiene_retencion && (
            <>
              <Input
                type="number"
                step="0.01"
                value={formData.importe_retencion}
                onChange={(e) => handleChange('importe_retencion', parseFloat(e.target.value) || 0)}
                className="w-24"
                placeholder="Importe"
              />
              <span className="text-sm">€</span>
            </>
          )}
        </div>

        <div className="bg-slate-100 rounded-lg p-4">
          <div className="flex justify-between font-bold text-lg">
            <span>Total Factura:</span>
            <span>{formData.total_factura.toFixed(2)} €</span>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <h3 className="font-semibold text-slate-900 border-b pb-2">Pago</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Método de Pago</Label>
            <Select value={formData.metodo_pago} onValueChange={(v) => handleChange('metodo_pago', v)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {METODOS_PAGO.map(m => (
                  <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center space-x-2 mt-6">
            <Checkbox
              id="pagado"
              checked={formData.pagado}
              onCheckedChange={(checked) => handleChange('pagado', checked)}
            />
            <Label htmlFor="pagado" className="cursor-pointer">Ya está pagado</Label>
          </div>
        </div>
        {formData.pagado && (
          <div className="w-48">
            <Label>Fecha de Pago</Label>
            <Input
              type="date"
              value={formData.fecha_pago}
              onChange={(e) => handleChange('fecha_pago', e.target.value)}
            />
          </div>
        )}
      </div>

      <div>
        <Label>Observaciones</Label>
        <Textarea
          value={formData.observaciones}
          onChange={(e) => handleChange('observaciones', e.target.value)}
          placeholder="Notas adicionales..."
          rows={2}
        />
      </div>

      <Button type="submit" className="w-full" data-testid="btn-submit-form">
        Continuar <ChevronRight className="w-4 h-4 ml-2" />
      </Button>
    </form>
  );
}
